import { create } from 'zustand';
import { api, setAccessToken } from './api-client.js';

/**
 * Auth and company context (SRS §2: "auth and company context in Zustand").
 * The permission set lives here so a component can ask `can('leave.approve')`
 * without re-deriving it; the server still authorizes every request.
 */

export interface SessionUser {
  id: string;
  email: string;
  fullName: string;
  employeeId: string | null;
  roles: string[];
  permissions: string[];
  mustResetPassword: boolean;
  twoFactorOn: boolean;
}

export interface SessionCompany {
  id: string;
  slug: string;
  displayName: string;
  currency: string;
  timezone: string;
  country: string;
  fiscalYearStart: number;
  branding: { primaryColour?: string | null; emailSenderName?: string | null; logoUrl?: string | null } | null;
  status: string;
}

/** A consented platform support session (FR-CP-14), shown as a banner. */
export interface SupportSession {
  grantId: string;
  reason: string;
  expiresAt: string;
  engineer: { fullName: string; email: string };
}

interface AuthState {
  user: SessionUser | null;
  company: SessionCompany | null;
  impersonation: SupportSession | null;
  permissions: Set<string>;
  status: 'unknown' | 'anonymous' | 'authenticated';
  can: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  /** Re-reads the session from /auth/me using the refresh cookie. */
  hydrate: () => Promise<void>;
  setSession: (payload: { accessToken: string; user: SessionUser; company?: SessionCompany }) => void;
  /** Enter a support session from the token the platform console handed over. */
  startSupportSession: (accessToken: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  company: null,
  impersonation: null,
  permissions: new Set<string>(),
  status: 'unknown',

  can: (permission) => get().permissions.has(permission),
  hasRole: (role) => get().user?.roles.includes(role) ?? false,

  hydrate: async () => {
    try {
      const me = await api<{ user: SessionUser; company: SessionCompany; impersonation?: SupportSession }>(
        '/auth/me',
      );
      set({
        user: me.user,
        company: me.company,
        impersonation: me.impersonation ?? null,
        permissions: new Set(me.user.permissions),
        status: 'authenticated',
      });
      applyBranding(me.company);
    } catch {
      set({ user: null, company: null, impersonation: null, permissions: new Set(), status: 'anonymous' });
    }
  },

  startSupportSession: async (accessToken) => {
    setAccessToken(accessToken, 'company');
    await get().hydrate();
  },

  setSession: ({ accessToken, user, company }) => {
    setAccessToken(accessToken, 'company');
    set({
      user,
      permissions: new Set(user.permissions),
      status: 'authenticated',
      ...(company ? { company } : {}),
    });
    if (company) applyBranding(company);
  },

  signOut: async () => {
    // A support session has no company account to log out of: signing out
    // ends the grant itself, so the token stops working everywhere at once.
    const path = get().impersonation ? '/support-access/session/end' : '/auth/logout';
    await api(path, { method: 'POST' }).catch(() => undefined);
    setAccessToken(null);
    set({ user: null, company: null, impersonation: null, permissions: new Set(), status: 'anonymous' });
  },
}));

/** Per-company white-labelling (FR-CP-21): drive the CSS variables. */
function applyBranding(company: SessionCompany): void {
  const colour = company.branding?.primaryColour;
  if (!colour) return;
  const rgb = hexToRgb(colour);
  if (rgb) document.documentElement.style.setProperty('--brand', rgb);
}

function hexToRgb(hex: string): string | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const n = parseInt(match[1] as string, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}
